import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader, RefreshCw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [purchase, setPurchase] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const sessionId = searchParams.get('session_id');
  const token = localStorage.getItem('token');

  const checkPaymentStatus = useCallback(async () => {
    try {
      console.log(`Checking payment status for session ${sessionId}`);
      const response = await axios.get(`${API}/purchases/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      console.log('Payment status response:', data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        setPurchase(data);
        toast.success('Payment successful! Tour unlocked.');
        // Referral funnel: attribute this purchase to a stored ref_code
        // (if any). Silent server no-op when no code is set.
        try {
          const cached = localStorage.getItem('audiostride_ref');
          let refCode = null;
          if (cached) {
            try {
              const parsed = JSON.parse(cached);
              if (parsed?.code && (!parsed.expires_at || parsed.expires_at > Date.now())) {
                refCode = parsed.code;
              }
            } catch (_) { refCode = cached; }
          }
          if (refCode) {
            axios.post(`${API}/referrals/track-event`, {
              event_type: 'tour_purchased',
              ref_code: refCode,
              tour_id: data.tour_id,
              amount: data.amount || data.amount_total || null,
              metadata: { purchase_id: data.purchase_id },
            }).catch(() => {});
          }
        } catch (_) {}
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Please log in to view your purchase');
        navigate('/');
        return true; // Stop polling
      }
      return false;
    }
  }, [sessionId, token, navigate]);

  const pollPaymentStatus = useCallback(async (currentAttempt = 0) => {
    const maxAttempts = 20;
    const pollInterval = 1000; // Poll every 1 second

    setAttempts(currentAttempt);

    if (currentAttempt >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    const success = await checkPaymentStatus();
    if (!success && currentAttempt < maxAttempts) {
      setTimeout(() => pollPaymentStatus(currentAttempt + 1), pollInterval);
    }
  }, [checkPaymentStatus]);

  const handleManualRetry = async () => {
    setIsRetrying(true);
    setStatus('checking');
    const success = await checkPaymentStatus();
    if (!success) {
      // Try a few more times
      let retrySuccess = false;
      for (let i = 0; i < 5; i++) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        retrySuccess = await checkPaymentStatus();
        if (retrySuccess) break;
      }
      if (!retrySuccess) {
        setStatus('timeout');
      }
    }
    setIsRetrying(false);
  };

  useEffect(() => {
    if (sessionId && token) {
      pollPaymentStatus(0);
    } else if (!token) {
      toast.error('Please log in to complete your purchase');
      navigate('/');
    } else {
      navigate('/');
    }
  }, [sessionId, token, navigate, pollPaymentStatus]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {status === 'checking' && (
          <div className="bg-white rounded-2xl shadow-card p-8 text-center">
            <Loader className="w-16 h-16 text-teal-500 mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2 font-sans">Processing Payment</h2>
            <p className="text-slate-600 font-body mb-4">Please wait while we confirm your payment...</p>
            <p className="text-sm text-slate-400">Attempt {attempts + 1}/20</p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-white rounded-2xl shadow-card p-8 text-center">
            <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-teal-500" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2 font-sans">Payment Successful!</h2>
            <p className="text-slate-600 mb-6 font-body">Your tour has been unlocked for 7 days. You can start exploring right away!</p>
            <button
              onClick={() => navigate('/my-tours')}
              data-testid="go-to-my-tours-button"
              className="w-full bg-teal-500 hover:bg-teal-600 text-white py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
            >
              Start Your Tour Now
            </button>
          </div>
        )}

        {status === 'timeout' && (
          <div className="bg-white rounded-2xl shadow-card p-8 text-center">
            <div className="w-16 h-16 bg-yellow-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-10 h-10 text-yellow-500" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2 font-sans">Payment Verification Pending</h2>
            <p className="text-slate-600 mb-4 font-body">
              Your payment was received! We're just confirming the details. This usually takes a moment.
            </p>
            <p className="text-sm text-slate-500 mb-6">
              If your tour doesn't appear in "My Tours" within a few minutes, please contact support.
            </p>
            <div className="space-y-3">
              <button
                onClick={handleManualRetry}
                disabled={isRetrying}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans flex items-center justify-center disabled:opacity-50"
              >
                {isRetrying ? (
                  <>
                    <Loader className="w-5 h-5 mr-2 animate-spin" />
                    Checking...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2" />
                    Check Again
                  </>
                )}
              </button>
              <button
                onClick={() => navigate('/my-tours')}
                className="w-full bg-teal-500 hover:bg-teal-600 text-white py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all active:scale-95 font-sans"
              >
                Go to My Tours
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
